import {
  Descriptions,
  Badge,
  Col,
  Row,
  Typography,
  Input,
  Space,
  InputNumber,
  Select,
  Button,
  message,
  Popconfirm,
  Divider,
  List,
  Tag,
  Image,
} from "antd"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { MachineBreadcrumbs } from "./MachineBreadcrumbs"
import PageHeader from "../Elements/PageHeader"
import {
  ArrowRightOutlined,
  CloseCircleFilled,
  LikeOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons"
import moment from "moment"
import { OrderLocation } from "../Orders/OrderLocation"
// import UserCard from "../Elements/UserCard"
import ViewOrderDetailsModal from "./ViewOrderDetailsPlate"
import ViewOrderDetailsDieCut from "./ViewOrderDetailsDieCut"
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore"
import { db } from "../../firebase/config"
import { RollLocationDisplay } from "../Rolls/RollLocationDisplay"
import { TbTransferIn } from "react-icons/tb"
import SplitOrderDrawer from "./Split/SplitOrderDrawer"
import { useCalculateProjectionMeters } from "../../hooks/useCalculateProjectionMaterials"
import { formatNumberWithSpaces } from "../Orders/Helpers/FormatNumbersWithSpaces"
import useFetchMachineEmployeeList from "../../hooks/useFetchMachineEmployeeList"
import { deleteObject, getStorage, listAll, ref, uploadBytes } from "firebase/storage"

const { Text, Link } = Typography
const { Option } = Select
const { TextArea } = Input

export default function ViewOrderDetails() {
  const [usedMeters, setUsedMeters] = useState(0)
  const [finishComment, setFinishComment] = useState("")
  const [issueComment, setIssueComment] = useState("")
  const [optionSelect, setOptionSelect] = useState(1)

  //roll
  const [selectedRollDestination, setSelectedRollDestination] = useState(null)

  //current machine warehouse
  const [numberOfUsingMachine, setNumberOfUsingMachine] = useState(0)

  //disable button
  const [disableGrabButton, setDisableGrabButton] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()

  const data = location.state && location.state
  const product = data && data.selectedProduct
  const currentMachine = data && product.machine.toLowerCase()

  //total meters for a job
  const { totalMeters } = useCalculateProjectionMeters(
    data.labelCount,
    data.selectedProduct.knifeCode,
  )

  const [rolls, setRolls] = useState([])
  const [currentRollMeters, setCurrentRollMeters] = useState(0)

  const [printedBy, setPrintedBy] = useState(data?.printedByName ?? undefined)

  const [settingActive, setSettingActive] = useState(false)

  // from data.selectedProduct.machine, e.g. "viva"
  const rawMachine = data.selectedProduct.machine.toLowerCase()

  // capitalize first letter
  const capitalMachine = rawMachine.charAt(0).toUpperCase() + rawMachine.slice(1)

  const { options: printerOptions, loading: loadingPrinters } =
    useFetchMachineEmployeeList(capitalMachine)

  // figure out which machine this page is for (e.g. "viva", "arsoma", etc.)
  // figure out which machine this page is for (e.g. "Viva" -> "currentlyInUseForViva", etc.)
  const activeField = `currentlyInUseFor${capitalMachine}`
  const startedAtField = `jobStartedAt${capitalMachine}`
  const totalSecField = `jobTotalSec${capitalMachine}`

  // hold the one order that’s currently in use on this machine
  const [activeOrder, setActiveOrder] = useState(null)

  useEffect(() => {
    const q = query(collection(db, "orders"), where(activeField, "==", true))
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) {
        setActiveOrder(null)
      } else {
        const doc = snap.docs[0]
        setActiveOrder({ id: doc.id, ...doc.data() })
      }
    })
    return () => unsub()
  }, [activeField])

  useEffect(() => {
    const rollsQuery = query(
      collection(db, "rolls"),
      where("inUse", "==", true),
      where("assignedOrderId", "array-contains", data.id),
    )
    const unsubscribe = onSnapshot(rollsQuery, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const data = doc.data()
        results.push({ ...data, key: doc.id, id: doc.id })
      })
      setRolls(results)
    })
    return () => unsubscribe()
  }, [data.id])

  //sets the state of currently using machine
  useEffect(() => {
    if (data.orderStatus === 3) {
      setNumberOfUsingMachine(1)
    } else if (data.orderStatus === 4) {
      setNumberOfUsingMachine(2)
    } else {
      setNumberOfUsingMachine(0)
    }
  }, [data])

  const running = !!(activeOrder && activeOrder.id === data.id)

  const handleMakeCurrent = async () => {
    if (settingActive) return
    setSettingActive(true)
    try {
      const batch = writeBatch(db)

      // 1) clear any other active on this machine
      const q = query(collection(db, "orders"), where(activeField, "==", true))
      const snap = await getDocs(q)
      snap.forEach((d) => {
        batch.update(d.ref, { [activeField]: false })
      })

      // 2) set THIS order active + start time
      const thisRef = doc(db, "orders", data.id)
      batch.update(thisRef, {
        [activeField]: true,
        [startedAtField]: serverTimestamp(),
        // NOTE: do NOT reset totalSecField here if you want to accumulate across pauses
      })

      await batch.commit()
      message.success("This order is now currently printing")
    } catch (err) {
      console.error(err)
      message.error("Failed to make current printing")
    } finally {
      setSettingActive(false)
    }
  }

  const onSelectPrinter = async (val) => {
    setPrintedBy(val)
    try {
      await updateDoc(doc(db, "orders", data.id), {
        printedByName: val,
        printedBySetAt: serverTimestamp(),
      })
      message.success("Printer saved.")
    } catch (err) {
      console.error(err)
      message.error("Failed to save printer.")
    }
  }

  //update roll
  const updateRollData = async (roll) => {
    const rollRef = doc(db, "rolls", roll.id)

    try {
      await updateDoc(rollRef, {
        rollLocation: selectedRollDestination,
      }).then(() => {
        message.success("Updated!")
      })
    } catch (err) {
      message.error(err.message)
    }
  }

  //updates the roll location
  const updateAllRollLocations = async (currentMachine) => {
    const batch = writeBatch(db)

    rolls.forEach((roll) => {
      const rollRef = doc(db, "rolls", roll.id)
      batch.update(rollRef, { rollLocation: numberOfUsingMachine })
    })

    await batch.commit()

    message.success("All roll locations updated!")
  }

  //updates doc
  const updateOrder = async () => {
    const orderRef = doc(db, "orders", data.id)

    const orderUpdatePayload = {
      machineIssues: {
        issue: optionSelect !== 1,
        issueComment,
      },
      doneByMachine: serverTimestamp(),
      machineHadIssues: optionSelect !== 1,
      orderStatus: optionSelect === 1 ? 5 : 2,
      [activeField]: false,
    }

    if (printedBy) {
      orderUpdatePayload.printedByName = printedBy
      orderUpdatePayload.printedBySetAt = serverTimestamp()
    }

    let elapsedAdd = 0
    const startedAt = data?.[startedAtField]?.seconds
    if (startedAt) {
      const nowSec = Math.floor(Date.now() / 1000)
      elapsedAdd = Math.max(0, nowSec - startedAt)
    }
    const baseSec = Number(data?.[totalSecField] ?? 0)
    const newTotal = baseSec + elapsedAdd

    orderUpdatePayload[totalSecField] = newTotal
    orderUpdatePayload[`jobFinishedAt${capitalMachine}`] = serverTimestamp()

    if (optionSelect === 1) {
      orderUpdatePayload.machineCompletion = {
        usedMeters,
        finishComment,
        machineIssues: {
          issue: false,
          issueComment,
        },
      }

      try {
        const sequenceDocRef = doc(db, "sequence", "documentForWinderSequence")
        const sequenceDocSnap = await getDoc(sequenceDocRef)

        if (sequenceDocSnap.exists()) {
          const sequenceData = sequenceDocSnap.data().winderSequence
          if (!sequenceData.includes(data.id)) {
            const updatedSequence = [...sequenceData, data.id]
            await updateDoc(sequenceDocRef, { winderSequence: updatedSequence })
          }
        } else {
          console.error("No sequence document found")
        }
      } catch (error) {
        console.error("Failed to update winder sequence:", error)
        message.error("Failed to update winder sequence.")
      }

      // 👇 clean up plate folder when finishing the order
      const plateCode = data.selectedPlate?.code
      const machine = data.selectedProduct?.machine?.toLowerCase()

      if (plateCode && machine) {
        try {
          const storage = getStorage()
          const folderRef = ref(storage, `${machine}Plates/${plateCode}`)
          const folderContents = await listAll(folderRef)

          // create .keep if it doesn't exist yet
          const hasKeep = folderContents.items.some((f) => f.name.endsWith(".keep"))
          if (!hasKeep) {
            const keepRef = ref(storage, `${machine}Plates/${plateCode}/.keep`)
            const emptyBlob = new Blob([""], { type: "text/plain" })
            await uploadBytes(keepRef, emptyBlob)
          }

          // delete everything except .keep
          const deletePromises = folderContents.items
            .filter((fileRef) => !fileRef.name.endsWith(".keep"))
            .map((fileRef) => deleteObject(fileRef))

          await Promise.all(deletePromises)
        } catch (err) {
          console.error("Failed to clean up plate folder:", err)
          // non-blocking — don't stop the order from finishing
        }
      }
    }

    try {
      await updateDoc(orderRef, orderUpdatePayload)
      message.success(optionSelect === 1 ? "Order updated!" : "Comment sent!")

      if (data.splitOrder) {
        const secondOrderRef = doc(db, "orders", data.otherOrderId)
        const secondDocsnap = await getDoc(secondOrderRef)
        const secondOrder = { id: secondDocsnap.id, ...secondDocsnap.data() }

        if (secondOrder.orderStatus >= 5) {
          for (const roll of rolls) {
            const rollRef = doc(db, "rolls", roll.id)
            await updateDoc(rollRef, {
              inUse: false,
              currentlyUsing: false,
              assignedOrderId: null,
              assignedTo: null,
              updated: serverTimestamp(),
            })
          }
        } else {
          for (const roll of rolls) {
            const rollRef = doc(db, "rolls", roll.id)
            await updateDoc(rollRef, { assignedOrderId: [secondOrder.id] })
          }
        }
      } else {
        for (const roll of rolls) {
          const rollRef = doc(db, "rolls", roll.id)
          await updateDoc(rollRef, {
            inUse: false,
            currentlyUsing: false,
            assignedOrderId: null,
            assignedTo: null,
            updated: serverTimestamp(),
          })
        }
      }

      navigate(`/${currentMachine}/orders`)
    } catch (error) {
      message.error(error.message)
    }
  }

  const commentSend = () => {
    if (optionSelect === 1) {
      return (
        <>
          <Text>Comment on finishing the order :</Text>
          <TextArea
            value={finishComment}
            style={{ width: "100%" }}
            onChange={(event) => setFinishComment(event.target.value)}
          />
        </>
      )
    } else {
      return (
        <>
          <Text>Comment explaining the problem :</Text>
          <TextArea
            value={issueComment}
            style={{ width: "100%" }}
            onChange={(event) => setIssueComment(event.target.value)}
          />
        </>
      )
    }
  }

  //handle roll location select
  const handleRollLocationSelect = (value) => {
    setSelectedRollDestination(value)
  }
  //grab this roll
  const handleGrab = async (roll) => {
    const rollRef = doc(db, "rolls", roll.id)
    updateDoc(rollRef, {
      currentlyUsing: true,
    })
      .then(() => {
        message.success("Grabbed")
      })
      .catch((error) => {
        message.error(error)
      })
  }

  //cancel grab
  const cancelGrab = async (roll, metersUsed) => {
    const rollRef = doc(db, "rolls", roll.id)
    updateDoc(rollRef, {
      currentlyUsing: false,
      remainingMeters: roll.remainingMeters - metersUsed,
    })
      .then(() => {
        message.success("Stopped using")
      })
      .catch((error) => {
        message.error(error)
      })
  }

  //gets desc data for roll transfers
  const transferPopover = (data) => {
    return (
      <div style={{ width: 450, paddingBottom: 15 }}>
        <Space>
          Transfer from
          <Text mark italic>
            {RollLocationDisplay(data)}
          </Text>
          to :
          <Select
            style={{ width: 250, zIndex: 10 }}
            dropdownStyle={{ zIndex: 9999 }} // Add this line
            size="small"
            onChange={handleRollLocationSelect}
            options={[
              {
                value: 0,
                label: "Warehouse",
              },
              {
                value: 1,
                label: "Arsoma Warehouse",
              },
              {
                value: 2,
                label: "Viva Warehouse",
              },
            ]}
          />
        </Space>
      </div>
    )
  }

  // useEffect(() => {
  //   console.log(data)
  // }, [data])

  //handle meter change
  const handleRollRemainingMeters = (value) => {
    //maps trought the rolls, finds the correct roll by id and updates itsvalue
    setCurrentRollMeters(value)
  }

  //grab roll popover
  const stopUsingRollPopover = (roll) => {
    return (
      <div style={{ padding: 15 }}>
        <Space>
          <Text>Meters :</Text>
          <InputNumber
            min={0}
            max={roll.originallMeters}
            defaultChecked={roll.remainingMeters}
            onChange={(value) => handleRollRemainingMeters(value, roll)}
            style={{ width: 250 }}
          />
        </Space>
      </div>
    )
  }

  //returns correct layout for button grabbed
  const returnGrabLayout = (item) => {
    if (item.currentlyUsing) {
      return (
        <>
          <Space>
            <Tag color="#f5222d" icon={<ReloadOutlined spin />}>
              Currently Using This Roll
            </Tag>
            <Popconfirm
              onConfirm={() => cancelGrab(item, currentRollMeters)}
              title="How many meters were used from the roll?"
              description={stopUsingRollPopover(item)}>
              <Button size="small" icon={<CloseCircleFilled />}>
                Stop Using
              </Button>
            </Popconfirm>
          </Space>
        </>
      )
    } else {
      const disabledByLocation = returnButtonDisable(item)
      const disabled = disabledByLocation || disableGrabButton || !running

      return (
        <Button
          disabled={disabled}
          size="small"
          icon={<LikeOutlined />}
          onClick={() => {
            if (!running) {
              message.warning("Set this order as currently printing first.")
              return
            }
            handleGrab(item)
          }}>
          Grab this roll
        </Button>
      )
    }
  }

  //returns correct disable for button
  const returnButtonDisable = (item) => {
    if (numberOfUsingMachine !== item.rollLocation) {
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    const isCurrentlyUsing = rolls.some((object) => object.currentlyUsing === true)

    if (isCurrentlyUsing) {
      setDisableGrabButton(true)
    } else {
      setDisableGrabButton(false)
    }
  }, [rolls])

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>{MachineBreadcrumbs(data.id, currentMachine)}</Col>
        <Col span={24}>
          <PageHeader text="Order Information" icon={<ShoppingCartOutlined />} />
        </Col>
        <Col span={24}>
          <div className="active-box" style={{ marginBottom: 16 }}>
            {activeOrder && activeOrder.id === data.id ? (
              // This order _is_ the active one
              <Space split={<Divider type="vertical" />} align="center" wrap>
                <Badge color="red" text="Currently in use" />

                <Typography.Text ellipsis={{ tooltip: activeOrder.client.client }}>
                  {activeOrder.client.client}
                </Typography.Text>

                <Typography.Text
                  ellipsis={{ tooltip: activeOrder.selectedProduct.product.productName }}>
                  {activeOrder.selectedProduct.product.productName}
                </Typography.Text>

                <Typography.Text>{formatNumberWithSpaces(activeOrder.labelCount)}</Typography.Text>

                <Typography.Text>{activeOrder.selectedProduct.labelWidth}</Typography.Text>

                <Typography.Text>{activeOrder.selectedProduct.labelHeight}</Typography.Text>

                <Typography.Text>{activeOrder.selectedProduct.formatWidth}</Typography.Text>

                <Typography.Text>{activeOrder.projectedMeters} m</Typography.Text>

                <Typography.Text ellipsis={{ tooltip: activeOrder.selectedProduct.material }}>
                  {activeOrder.selectedProduct.material}
                </Typography.Text>
              </Space>
            ) : (
              // This order is _not_ active (or none is active)
              <Space split={<Divider type="vertical" />} align="center" wrap>
                {activeOrder ? (
                  <Typography.Text type="secondary">
                    Order {activeOrder.orderNumber} is currently printing
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">
                    No order is currently active on {capitalMachine}
                  </Typography.Text>
                )}

                <Button type="primary" onClick={handleMakeCurrent} loading={settingActive}>
                  Make this currently printing
                </Button>
              </Space>
            )}
          </div>
        </Col>

        <Col span={15}>
          <Space direction="vertical">
            {/* <UserCard orderManager={data && data.userData.salesManager} /> */}
            <Descriptions
              column={8}
              bordered
              className="machineFillDesc"
              size="small"
              layout="vertical"
              title="Technical Details">
              <Descriptions.Item label="Label Count">
                {formatNumberWithSpaces(data.labelCount)}
              </Descriptions.Item>
              <Descriptions.Item label="Die-Cut">
                <ViewOrderDetailsDieCut knifeCode={product.knifeCode} knifeId={product.knifeId} />
              </Descriptions.Item>
              <Descriptions.Item label="Width">{product.labelWidth}</Descriptions.Item>
              <Descriptions.Item label="Height">{product.labelHeight}</Descriptions.Item>
              <Descriptions.Item label="Mat. Width">{product.formatWidth}</Descriptions.Item>
              <Descriptions.Item label="Proj. meters">
                {totalMeters && formatNumberWithSpaces(totalMeters)} m
              </Descriptions.Item>
              <Descriptions.Item label="Material">{product.material}</Descriptions.Item>
            </Descriptions>

            <Descriptions column={3} size="small" title="Order Details" bordered>
              <Descriptions.Item label="Client">{product.client}</Descriptions.Item>
              <Descriptions.Item label="Product">{product.product.productName}</Descriptions.Item>
              <Descriptions.Item label="Order time">
                {moment.unix(data.created.seconds).format("LLL")}
              </Descriptions.Item>

              <Descriptions.Item label="Sales Comment" span={3}>
                {data.salesComment}
              </Descriptions.Item>
              <Descriptions.Item label="Designer Comment" span={3}>
                {data.designerComment}
              </Descriptions.Item>
              <Descriptions.Item label="Paper Manager Comment" span={3}>
                {data.PMComment}
              </Descriptions.Item>
            </Descriptions>
            <Descriptions column={4} bordered size="small" title="Plate">
              <Descriptions.Item label="Technical Page">
                <Link href={data.technicalPage} target="_blank">
                  View
                </Link>
              </Descriptions.Item>

              {product.advanced.changableSpace ? (
                <Descriptions.Item contentStyle={{ color: "red" }} label="Changable Space Data">
                  {data.changableSpaceData}
                </Descriptions.Item>
              ) : (
                ""
              )}

              <Descriptions.Item label="Plate Number">
                {data.selectedPlate ? <ViewOrderDetailsModal props={data.selectedPlate} /> : ""}
              </Descriptions.Item>
            </Descriptions>
            <Descriptions bordered size="small" column={1} title="Order Completion">
              <Descriptions.Item label="Total Used Meters">
                <InputNumber
                  min={0}
                  // max={rolls && totalRemainingMeters}
                  style={{ width: "100%" }}
                  size="small"
                  value={usedMeters}
                  onChange={setUsedMeters}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Fill the done details">
                <Space.Compact style={{ width: 250 }} size="small" block>
                  <Select
                    size="small"
                    defaultValue={1}
                    onChange={(value) => setOptionSelect(value)}
                    style={{
                      width: "100%",
                    }}>
                    <Option value={0}>Send Back to Paper Manager</Option>
                    <Option value={1}>Finish the Order</Option>
                  </Select>
                  <Button
                    disabled={usedMeters <= 0 || !printedBy}
                    onClick={updateOrder}
                    type="primary">
                    Send
                  </Button>
                </Space.Compact>
              </Descriptions.Item>
              <Descriptions.Item label="Who Printed?">
                <Select
                  size="small"
                  value={printedBy}
                  placeholder={loadingPrinters ? "Loading..." : "Select printer"}
                  loading={loadingPrinters}
                  onChange={onSelectPrinter}
                  style={{ width: 250 }}
                  options={printerOptions}
                  allowClear
                />
                {!printedBy && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    Select a printer to enable “Send”.
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Split Order">
                <SplitOrderDrawer disabled={data.splitOrder} orderData={data} rollData={rolls} />
              </Descriptions.Item>
            </Descriptions>
            {commentSend()}
          </Space>
        </Col>
        <Col span={9}>
          <Image src={data.technicalPage} width={500} />
        </Col>

        <Col span={24}>
          <Divider orientation="left">Roll Details</Divider>
          <Button
            type="primary"
            icon={<TbTransferIn />}
            size="small"
            onClick={() => updateAllRollLocations(data.orderStatus)}>
            Transfer all rolls to {OrderLocation(data.orderStatus)}
          </Button>
          <List
            itemLayout="horizontal"
            dataSource={rolls}
            style={{ paddingBottom: 50 }}
            renderItem={(item, index) => (
              <List.Item
                style={{ padding: 25 }}
                actions={[
                  <Space split={<Divider type="vertical" />}>
                    {returnGrabLayout(item)}
                    <Popconfirm
                      onConfirm={() => updateRollData(item)}
                      title="Change the location?"
                      description={() => transferPopover(item.rollLocation)}>
                      <Button type="primary" size="small">
                        Change roll location <ArrowRightOutlined />
                      </Button>
                    </Popconfirm>
                  </Space>,
                ]}>
                <List.Item.Meta
                  title={<Text copyable={{ text: item.id }}>Roll id : {item.id}</Text>}
                  description={
                    <Space split={<Divider type="vertical" />}>
                      <Badge status="processing" text={RollLocationDisplay(item.rollLocation)} />
                      <Text>
                        Material : <Tag color="blue">{item.material.material}</Tag>
                      </Text>
                      <Text>
                        Width : <Tag color="blue">{item.rollWidth}</Tag>
                      </Text>
                      <Text>
                        Remaining Meters : <Tag color="blue">{item.remainingMeters}</Tag>
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Col>
        <Col span={24}></Col>
      </Row>
    </div>
  )
}
